<script setup lang="ts">
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import {computed, ref, watch} from "vue";
import {consumerProductApi} from "@/container";
import FileUrl from "@/core/interfaces/FileUrl";
import SecurePrescriptionPages from "@/core/components/secure-image/SecurePrescriptionPages.vue";
import {Calendar, ChevronLeft, ChevronRight, FileText} from "@lucide/vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";

const props = defineProps<{
    consumerKey: string;
    product: ProductSummary;
}>();

const emit = defineEmits<{
    (e: 'lastDate', date: Date | null): void;
    (e: 'currentFile', file: FileUrl | null, blob: Blob | null): void;
}>();

interface Prescription {
    key: string;
    files: FileUrl[];
    date?: string | null;
}

const prescriptions = ref<Prescription[] | null>(null);
const loading = ref(false);
const currentIndex = ref(0);

const current = computed(() => prescriptions.value?.[currentIndex.value] ?? null);
const hasPrev = computed(() => currentIndex.value > 0);
const hasNext = computed(() => currentIndex.value < (prescriptions.value?.length ?? 0) - 1);

/**
 * Reset the emitted current file whenever the displayed prescription changes.
 * SecurePrescriptionPages will emit `loaded` with the blob of the first page when the bytes are in.
 */
watch(current, () => {
    emit('currentFile', null, null);
});

const onImageLoaded = (blob: Blob) => {
    emit('currentFile', current.value?.files?.[0] ?? null, blob);
};

const onImageError = () => {
    emit('currentFile', null, null);
};

watch(() => props.product.id, (productId) => {
    if (!productId) {
        prescriptions.value = null;
        return;
    }

    currentIndex.value = 0;
    loading.value = true;

    consumerProductApi().salesIndex(props.consumerKey, {range: '1 year', products: [productId]})
        .then(data => {
            if (data && data.length > 0) {
                const product = data[0];
                prescriptions.value = product.history
                    .map((sale: any) => sale.prescription ? ({
                        ...sale.prescription,
                        date: sale.date ?? null,
                    }) : null)
                    .filter(Boolean);

                const lastDate = product.history.length > 0 ? product.history[0].date : null;
                emit('lastDate', lastDate ? new Date(lastDate) : null);
            } else {
                prescriptions.value = [];
            }
        })
        .finally(() => {
            loading.value = false;
        });
}, {immediate: true});
</script>

<template>
  <div class="flex flex-col h-full">
    <div v-if="loading" class="flex-1 flex flex-col items-center justify-center gap-3 py-12 max-h-96">
      <div>
        <BaseFlexSpinner />
      </div>
      <p class="text-xs text-gray-400">
        Chargement...
      </p>
    </div>

    <div
      v-else-if="prescriptions !== null && !prescriptions.length"
      class="flex-1 flex flex-col items-center justify-center gap-3 py-12 text-center px-6 max-h-96"
    >
      <div class="size-12 rounded-full bg-gray-100 flex items-center justify-center">
        <FileText class="size-5 text-gray-300" />
      </div>
      <div>
        <p class="text-sm font-medium text-gray-500">
          Aucune ordonnance
        </p>
        <p class="text-xs text-gray-400 mt-0.5">
          sur les 12 derniers mois
        </p>
      </div>
    </div>

    <template v-else-if="prescriptions && prescriptions.length">
      <div class="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 bg-gray-50/80 shrink-0">
        <div class="flex-1 min-w-0 space-y-0.5">
          <div v-if="current?.date" class="flex items-center gap-1.5 text-xs text-gray-400">
            <Calendar class="size-3 shrink-0" />
            {{ $filters.date(current?.date, 'dayOnly') }}
          </div>
        </div>

        <div v-if="prescriptions.length > 1" class="flex items-center gap-1 shrink-0">
          <button
            class="size-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors"
            :disabled="!hasPrev"
            @click="currentIndex--"
          >
            <ChevronLeft class="size-3.5" />
          </button>
          <span class="text-xs text-gray-500 tabular-nums w-8 text-center">
            {{ currentIndex + 1 }}/{{ prescriptions.length }}
          </span>
          <button
            class="size-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors"
            :disabled="!hasNext"
            @click="currentIndex++"
          >
            <ChevronRight class="size-3.5" />
          </button>
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto p-4">
        <div v-if="current?.files?.length" class="flex items-start justify-center">
          <SecurePrescriptionPages
            loader-type="skeleton"
            :urls="current!.files"
            class="w-full rounded-lg shadow-sm"
            @loaded="onImageLoaded"
            @error="onImageError"
          />
        </div>
        <div
          v-else
          class="w-full aspect-[3/4] rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-3 text-gray-300"
        >
          <FileText class="size-8" />
          <p class="text-xs text-gray-400">
            Image non disponible
          </p>
        </div>
        <div v-if="$slots['below-image']" class="px-4 py-3 border-t border-gray-100 shrink-0">
          <slot name="below-image" />
        </div>
      </div>
    </template>
  </div>
</template>
